import React, { ReactElement } from 'react';
import { useParams } from 'react-router-dom';

import { ElearningParamType } from '../../../routes';

import { LayoutSectionComponent } from '@/components/LayoutSection';
import { Card, Col, Divider, Spinner } from 'elmo-elements';
import {
  useDeleteCommentMutation,
  useGetFrameCommentsQuery,
  useUpdateCommentStatusMutation,
} from '@/modules/services';
import Comment from '@/components/Comment';
import CommentsType from '@/modules/types/comments';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useDispatch } from 'react-redux';

const Comments = () => {
  const dispatch = useDispatch();
  const { frameId } = useParams<ElearningParamType>();

  const [deleteComment] = useDeleteCommentMutation();
  const [updateCommentStatus] = useUpdateCommentStatusMutation();

  const { data: commentsData, isLoading } = useGetFrameCommentsQuery(
    frameId || '',
    {
      skip: !frameId || frameId === 'add',
    },
  );

  if (frameId === 'add') {
    return <div />;
  }

  const handleOnStatusUpdate = async (commentId: string, status: number) => {
    try {
      await updateCommentStatus({
        id: commentId,
        content: {
          status,
        },
      }).unwrap();
    } catch (error) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Something went wrong',
        },
      });
    }
  };

  const displayComments = (comments: CommentsType[]) =>
    comments.length > 0 ? (
      <>
        {comments.map((comment, index) => (
          <Col key={index} role="listitem">
            <Comment
              title={`${comment.reviewer.reviewerMetadata.firstName} ${comment.reviewer.reviewerMetadata.lastName}`}
              comment={comment}
              onDelete={(id) => {
                deleteComment(id);
              }}
              onUpdate={async (status) => {
                await handleOnStatusUpdate(comment.id, status);
              }}
            />

            <Divider />
          </Col>
        ))}
      </>
    ) : (
      <Card>No comment have been added yet.</Card>
    );

  return isLoading ? (
    <Spinner size="xl" ariaLabel="Loading comment data" />
  ) : (
    displayComments(commentsData || [])
  );
};

const ComponentWithLayoutSection = (): ReactElement => (
  <LayoutSectionComponent
    heading="Comments"
    content={<Comments />}
    isCollapsible={true}
  />
);

export default ComponentWithLayoutSection;
